import {Flex} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import * as React from 'react';

import notifications, {Response} from 'notifications';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import TextArea from 'ui-library/bs/TextArea';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import {GENERIC_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';
import {ValidationRuleParams, ValidationRulesErrorResponse} from 'validation_rules/network/types';
import {validateUserInputFormula} from 'validation_rules/network/ValidationRulesDAO';

import {ConditionEvaluatorVersion} from '../constants';

interface ValidationTextAreaProps {
  label: string;
  value: string;
  onChange: (newValue: string) => void;
  required?: boolean;
  maxCharacters?: number | null;
}

function ValidationTextArea({
  label,
  value,
  onChange,
  required = false,
  maxCharacters = null,
}: ValidationTextAreaProps): JSX.Element {
  return (
    <FormGroup>
      <Label required={required}>{label}</Label>
      <TextArea
        name={label}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        rows={2}
        required={required}
      />
      {maxCharacters ? (
        <p className={css(styles.validationCharacterCountText)}>
          {value.length}/{maxCharacters} characters
        </p>
      ) : (
        ''
      )}
    </FormGroup>
  );
}

interface ValidationRulesModalProps {
  hasValidationRulesItemId: string;
  onRequestClose: () => void;
  onRequestSubmit: (upsertParams: ValidationRuleParams) => Promise<void>;
  title?: string;
  name?: string;
  userInputFormula?: string;
  description?: string;
  errorMessage?: string | null;
  overridable?: boolean | null;
  negateResult?: boolean | null;
  errors?: Array<string> | null;
}

export default function ValidationRulesModal(props: ValidationRulesModalProps): JSX.Element {
  const [name, setName] = React.useState(props.name ?? '');
  const [description, setDescription] = React.useState(props.description ?? '');
  const [userInputFormula, setUserInputFormula] = React.useState(props.userInputFormula ?? '');
  const [errorMessage, setErrorMessage] = React.useState(props.errorMessage ?? '');
  const [errors, setErrors] = React.useState(props.errors ?? Array<string>);

  const negateResult = props.negateResult ?? false;

  async function handleOnRequestSubmit(): Promise<void> {
    await props
      .onRequestSubmit({
        name,
        description,
        userInputFormula,
        errorMessage,
        overridable: false,
        evaluatorVersion: ConditionEvaluatorVersion.V1,
        negateResult,
      })
      .catch((payload: ValidationRulesErrorResponse) => {
        if (payload.validationRuleErrors) {
          setErrors(payload.validationRuleErrors);
        } else {
          notifications.error(GENERIC_ERROR_MESSAGE);
        }
      });
  }

  async function handleValidateSyntax(): Promise<void> {
    await validateUserInputFormula(props.hasValidationRulesItemId, userInputFormula)
      .then(() => {
        setErrors([]);
      })
      .catch((resp: Response) => {
        if (resp.responseJSON) {
          const payload: ValidationRulesErrorResponse = resp.responseJSON as ValidationRulesErrorResponse;
          if (payload.validationRuleErrors) {
            setErrors(payload.validationRuleErrors);
          } else {
            notifications.error(GENERIC_ERROR_MESSAGE);
          }
        } else {
          notifications.notifyError(resp);
        }
      });
  }

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleOnRequestSubmit}
      title={props.title ?? 'Create rule'}
      medium={true}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
          style: ButtonStyle.TERTIARY_GRAY,
        },
        {
          text: 'Save',
          submit: true,
          style: ButtonStyle.SECONDARY,
        },
      ]}
    >
      <FormGroup>
        <TextField
          label="Name"
          value={name}
          dataTestAttrs={{
            component: 'ValidationRulesModal',
            element: 'inputText',
            key: 'name',
          }}
          onChange={(e: InputChangeEvent) => {
            setName(e.target.value);
          }}
          isRequired={true}
        />
      </FormGroup>
      <ValidationTextArea label="Description" value={description} onChange={setDescription} />
      <FormGroup>
        <Label>Rule definition</Label>
        <p>
          Rules are defined with a formula that evaluates to TRUE or FALSE. If FALSE, users will be shown the
          error message below.
        </p>
        <TextArea
          name="Definition"
          value={userInputFormula}
          onChange={(e) => {
            setUserInputFormula(e.target.value);
          }}
          rows={3}
        />
        {errors && (
          <InlineBannerStack
            messages={errors.map((text: string) => ({
              bannerType: InlineBannerType.ERROR,
              text,
            }))}
          />
        )}
      </FormGroup>
      <Flex justify="flex-end">
        <Button
          text="Check syntax"
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          size={ButtonSize.SMALL}
          onClick={handleValidateSyntax}
        />
      </Flex>
      <ValidationTextArea
        label="Error message"
        value={errorMessage}
        onChange={setErrorMessage}
        required={true}
        maxCharacters={250}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  validationCharacterCountText: {
    textAlign: 'right',
  },
});
